/**
 * 10.6E — LINGUAGEM DE GOLPE (geometria, não partícula).
 *
 * O combate só tinha faíscas: o golpe em si era invisível. Num jogo visto de cima, o que
 * lê o ataque é o RASTRO no chão — o clássico arco de corte. Este módulo desenha três
 * coisas, todas em malhas reaproveitadas de um pool (zero alocação por golpe):
 *
 *   · ARCO DE CORTE  — setor de anel deitado no chão, na abertura e no alcance reais da
 *     arma. Nasce fechado e um pouco atrás do ângulo de mira e "varre" até o fim do golpe,
 *     então lê como um corte e não como um adesivo que apareceu.
 *   · ONDA DE IMPACTO — anel que abre e some no ponto do acerto. Crítico abre maior.
 *   · CLARÃO DE TIRO  — disco curto na boca do arco quando solta a flecha.
 *
 * Tudo com blending aditivo e sem escrita de profundidade: brilha por cima do chão sem
 * brigar com a ordenação. Custo: 3 draw calls no pico, geometrias em cache por abertura.
 */
import * as THREE from "three";
import { events } from "../core/Events";
import { quality } from "../core/Quality";

interface Piece {
  mesh: THREE.Mesh;
  mat: THREE.MeshBasicMaterial;
  life: number;
  dur: number;
  kind: "slash" | "ring" | "flash" | "blade" | "beam";
  a0: number; // ângulo inicial da varredura (slash)
  sweep: number; // quanto varre até o fim (slash)
  s0: number; // escala inicial
  s1: number; // escala final
  op: number; // opacidade de pico
}

const MAX_PIECES = 14;
/** Cacos de morte: um único InstancedMesh reaproveitado em anel (o mais antigo cede a vez). */
const MAX_SHARDS = 64;
const shardGeo = new THREE.TetrahedronGeometry(0.32, 0);
const shardMat = new THREE.MeshStandardMaterial({ color: 0xffffff, emissive: 0x6a3bff, emissiveIntensity: 1.7, roughness: 0.35, flatShading: true });
const _o = new THREE.Object3D();
const _c = new THREE.Color();

interface Shard {
  life: number; dur: number; size: number;
  vx: number; vy: number; vz: number; // velocidade
  rx: number; ry: number; rz: number; // giro
  sx: number; sy: number; sz: number; // origem
}

/**
 * Pinta um degradê nos VÉRTICES. Com blending aditivo, escuro = transparente — então isto
 * é o que transforma um setor chapado (que lia como "holofote no chão") num CORTE de verdade:
 * aceso na beirada externa, apagando para dentro e nas duas pontas.
 */
function withColors(geo: THREE.BufferGeometry, f: (r: number, a: number) => number): THREE.BufferGeometry {
  const pos = geo.attributes.position;
  const col = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), y = pos.getY(i);
    const v = Math.max(0, Math.min(1, f(Math.hypot(x, y), Math.atan2(y, x))));
    col[i * 3] = v; col[i * 3 + 1] = v; col[i * 3 + 2] = v;
  }
  geo.setAttribute("color", new THREE.BufferAttribute(col, 3));
  return geo;
}

/** Setor de anel (unitário) por abertura — cacheado: as armas repetem poucas aberturas. */
const arcCache = new Map<number, THREE.BufferGeometry>();
function arcGeo(arc: number): THREE.BufferGeometry {
  const key = Math.round(arc * 20) / 20;
  let g = arcCache.get(key);
  if (!g) {
    const inner = 0.6;
    // raio 1 = alcance da arma; 4 anéis radiais para o degradê ficar liso
    const raw = new THREE.RingGeometry(inner, 1, Math.max(10, Math.round(key * 14)), 4, -key / 2, key);
    g = withColors(raw, (r, a) => {
      const fr = Math.max(0, (r - inner) / (1 - inner)); // 0 dentro → 1 na beirada
      const fa = Math.cos((a / (key / 2)) * (Math.PI / 2)); // 1 no meio → 0 nas pontas
      return fr * fr * Math.max(0, fa) ** 0.7;
    });
    arcCache.set(key, g);
  }
  return g;
}
/**
 * 10.9AZ — LÂMINA VOLUMÉTRICA: faixa vertical curva (setor de cilindro aberto) na altura da arma,
 * que varre junto com o arco do chão. Aceso na frente da varredura e na altura do gume, apagando
 * para trás e nas bordas de cima/baixo — lê como o "rastro" do golpe cortando o ar.
 */
const bladeCache = new Map<number, THREE.BufferGeometry>();
function bladeGeo(arc: number): THREE.BufferGeometry {
  const key = Math.round(arc * 20) / 20;
  let g = bladeCache.get(key);
  if (!g) {
    // raio 1 = alcance; altura 0.55 (escala junto); aberto (sem tampas); ângulo centrado em 0
    const raw = new THREE.CylinderGeometry(1, 1, 0.55, Math.max(10, Math.round(key * 14)), 3, true, -key / 2, key);
    const pos = raw.attributes.position;
    const col = new Float32Array(pos.count * 3);
    for (let i = 0; i < pos.count; i++) {
      const a = Math.atan2(pos.getX(i), pos.getZ(i)); // ângulo no setor (Cylinder mede a partir de +Z)
      const fy = 1 - Math.min(1, Math.abs(pos.getY(i)) / 0.275); // 1 no gume (meio) → 0 nas bordas
      const fa = Math.cos((a / (key / 2)) * (Math.PI / 2)); // 1 no meio → 0 nas pontas
      const v = Math.max(0, Math.min(1, fy * fy * Math.max(0, fa) ** 0.6));
      col[i * 3] = v; col[i * 3 + 1] = v; col[i * 3 + 2] = v;
    }
    raw.setAttribute("color", new THREE.BufferAttribute(col, 3));
    g = raw;
    bladeCache.set(key, g);
  }
  return g;
}
const ringGeo = withColors(new THREE.RingGeometry(0.72, 1, 28), (r) => 1 - Math.abs(r - 0.86) / 0.14);
const flashGeo = withColors(new THREE.CircleGeometry(1, 14), (r) => 1 - r);
/** 10.9GT — feixe de luz do loot lendário: cilindro aberto, aceso na base e apagando pra cima. */
const beamGeo = (() => {
  const g = new THREE.CylinderGeometry(1, 1, 1, 12, 1, true);
  const pos = g.attributes.position;
  const col = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const v = Math.max(0, 1 - (pos.getY(i) + 0.5)) ** 1.6;
    col[i * 3] = v; col[i * 3 + 1] = v; col[i * 3 + 2] = v;
  }
  g.setAttribute("color", new THREE.BufferAttribute(col, 3));
  return g;
})();

export class SlashFx {
  private readonly group = new THREE.Group();
  private readonly pieces: Piece[] = [];
  private readonly free: Piece[] = [];
  private readonly shards: THREE.InstancedMesh;
  private readonly shardState: Shard[] = [];
  private shardCursor = 0;

  constructor(scene: THREE.Scene) {
    scene.add(this.group);
    events.on("fx:slash", (d) => this.slash(d));
    events.on("fx:shot", (d) => this.shot(d));
    events.on("damage:dealt", (d) => {
      if (d.target !== "enemy") return;
      this.ring(d.position.x, 1.0, d.position.z, d.crit ? 0xffe08a : 0xc9b6ff, d.crit ? 2.1 : 1.25, d.crit ? 0.75 : 0.42);
    });
    // 10.6E — a morte era só encolher e subir. Agora a criatura ESTILHAÇA.
    events.on("enemy:killed", (d) => {
      this.shatter(d.position.x, 0.9, d.position.z, d.elite ? 0xffd24a : 0x9d7bff, d.elite ? 20 : 12, d.elite ? 1.5 : 1);
      this.ring(d.position.x, 0.3, d.position.z, d.elite ? 0xffd24a : 0x9d7bff, d.elite ? 3.4 : 2.0, 0.6);
    });
    this.shards = new THREE.InstancedMesh(shardGeo, shardMat, MAX_SHARDS);
    this.shards.frustumCulled = false;
    this.shards.count = MAX_SHARDS;
    for (let i = 0; i < MAX_SHARDS; i++) {
      this.shardState.push({ life: 0, dur: 1, size: 1, vx: 0, vy: 0, vz: 0, rx: 0, ry: 0, rz: 0, sx: 0, sy: 0, sz: 0 });
      _o.position.set(0, -999, 0);
      _o.scale.setScalar(0.0001);
      _o.updateMatrix();
      this.shards.setMatrixAt(i, _o.matrix);
    }
    this.shards.instanceMatrix.needsUpdate = true;
    scene.add(this.shards);
  }

  /** Explode a criatura em cacos que voam, giram e somem encolhendo. 1 draw call no total. */
  shatter(x: number, y: number, z: number, color: number, count: number, size: number): void {
    if (!this.on) return;
    const n = Math.round(count * quality.values.particleScale);
    for (let k = 0; k < n; k++) {
      const i = this.shardCursor;
      this.shardCursor = (this.shardCursor + 1) % MAX_SHARDS;
      const s = this.shardState[i];
      const a = Math.random() * Math.PI * 2;
      const up = 2.2 + Math.random() * 3.4;
      const out = 1.6 + Math.random() * 3.2;
      s.dur = 0.55 + Math.random() * 0.35;
      s.life = s.dur;
      s.size = size * (0.6 + Math.random() * 1.0);
      s.sx = x; s.sy = y; s.sz = z;
      s.vx = Math.cos(a) * out; s.vy = up; s.vz = Math.sin(a) * out;
      s.rx = (Math.random() - 0.5) * 14; s.ry = (Math.random() - 0.5) * 14; s.rz = (Math.random() - 0.5) * 14;
      this.shards.setColorAt(i, _c.setHex(color).multiplyScalar(0.7 + Math.random() * 0.6));
    }
    if (this.shards.instanceColor) this.shards.instanceColor.needsUpdate = true;
  }

  private get on(): boolean {
    return quality.values.particleScale > 0;
  }

  private acquire(geo: THREE.BufferGeometry, color: number, op: number): Piece | null {
    if (this.pieces.length >= MAX_PIECES) return null;
    let p = this.free.pop();
    if (!p) {
      const mat = new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, vertexColors: true });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.rotation.x = -Math.PI / 2; // deitado no chão
      this.group.add(mesh);
      p = { mesh, mat, life: 0, dur: 1, kind: "ring", a0: 0, sweep: 0, s0: 1, s1: 1, op };
    }
    p.mesh.geometry = geo;
    p.mesh.rotation.set(-Math.PI / 2, 0, 0); // 10.9AZ: a lâmina deixa a peça em pé — volta ao chão ao reusar
    p.mat.color.setHex(color);
    p.mat.opacity = op;
    p.op = op;
    p.mesh.visible = true;
    this.pieces.push(p);
    return p;
  }

  /** Arco de corte no chão, varrendo do começo ao fim do golpe. */
  private slash(d: { x: number; z: number; angle: number; arc: number; range: number; color: number; heavy: boolean }): void {
    if (!this.on) return;
    const arc = Math.max(0.25, Math.min(Math.PI * 1.1, d.arc || Math.PI * 0.6));
    const p = this.acquire(arcGeo(arc), d.color, d.heavy ? 0.62 : 0.46);
    if (!p) return;
    p.kind = "slash";
    p.dur = d.heavy ? 0.3 : 0.22;
    p.life = p.dur;
    p.sweep = arc * 0.55; // varre um pouco além da abertura: sensação de corte
    p.a0 = d.angle - p.sweep * 0.5;
    p.s0 = d.range * 0.82;
    p.s1 = d.range * 1.06;
    p.mesh.position.set(d.x, 0.16, d.z);
    p.mesh.scale.setScalar(p.s0);
    // RingGeometry mede o ângulo a partir de +X e cresce anti-horário; o mundo mede a
    // mira a partir de +Z e cresce horário. rotation.z = -(ângulo) + PI/2 casa os dois.
    p.mesh.rotation.z = Math.PI / 2 - p.a0;
    // 10.9AZ — lâmina no ar (custa 1 draw call a mais; some em qualidade baixa)
    if (quality.values.particleScale >= 0.75) this.blade(d, arc);
  }

  /** Faixa vertical curva na altura da arma, varrendo com o golpe. */
  private blade(d: { x: number; z: number; angle: number; range: number; color: number; heavy: boolean }, arc: number): void {
    const p = this.acquire(bladeGeo(arc), d.color, d.heavy ? 0.5 : 0.36);
    if (!p) return;
    p.kind = "blade";
    p.dur = d.heavy ? 0.26 : 0.19;
    p.life = p.dur;
    p.sweep = arc * 0.55;
    p.a0 = d.angle - p.sweep * 0.5;
    p.s0 = d.range * 0.78;
    p.s1 = d.range * 1.0;
    p.mesh.position.set(d.x, d.heavy ? 1.15 : 1.0, d.z);
    p.mesh.rotation.set(0, p.a0, 0); // em pé: só gira em Y (o cilindro já é vertical)
    p.mesh.scale.set(p.s0, d.heavy ? 1.3 : 1, p.s0);
  }

  /** 10.9GT — LOOT LENDÁRIO: feixe de luz subindo do chão + anel, na cor da raridade (~1,4 s). */
  lootBeam(x: number, z: number, color: number, size = 1): void {
    if (!this.on) return;
    this.ring(x, 0.08, z, color, 3.6 * size, 0.6);
    const p = this.acquire(beamGeo, color, 0.75);
    if (!p) return;
    p.kind = "beam";
    p.dur = 1.4;
    p.life = p.dur;
    p.s0 = 0.25 * size;
    p.s1 = 0.7 * size;
    p.mesh.position.set(x, 3.0, z);
    p.mesh.rotation.set(0, 0, 0);
    p.mesh.scale.set(p.s0, 6, p.s0);
  }

  /** Onda de impacto no ponto do acerto. */
  ring(x: number, y: number, z: number, color: number, size: number, op: number): void {
    if (!this.on) return;
    const p = this.acquire(ringGeo, color, op);
    if (!p) return;
    p.kind = "ring";
    p.dur = 0.26;
    p.life = p.dur;
    p.s0 = size * 0.25;
    p.s1 = size;
    p.mesh.position.set(x, y, z);
    p.mesh.rotation.z = 0;
    p.mesh.scale.setScalar(p.s0);
  }

  /** Clarão curto na boca da arma ao disparar. */
  private shot(d: { x: number; y: number; z: number; color: number }): void {
    if (!this.on) return;
    const p = this.acquire(flashGeo, d.color, 0.7);
    if (!p) return;
    p.kind = "flash";
    p.dur = 0.13;
    p.life = p.dur;
    p.s0 = 0.42;
    p.s1 = 0.14;
    p.mesh.position.set(d.x, d.y, d.z);
    p.mesh.rotation.z = 0;
    p.mesh.scale.setScalar(p.s0);
  }

  update(dt: number): void {
    // cacos: integra posição/giro, encolhe no fim e esconde
    let dirty = false;
    for (let i = 0; i < MAX_SHARDS; i++) {
      const s = this.shardState[i];
      if (s.life <= 0) continue;
      s.life -= dt;
      dirty = true;
      const age = s.dur - s.life;
      // balística fechada a partir da velocidade INICIAL (nada de acumular por frame:
      // assim o caco desenha o mesmo arco em qualquer taxa de quadros)
      const px = s.sx + s.vx * age;
      const py = s.sy + s.vy * age - 7.5 * age * age;
      const pz = s.sz + s.vz * age;
      const k = Math.max(0, s.life / s.dur);
      _o.position.set(px, Math.max(0.09, py), pz);
      _o.rotation.set(s.rx * age, s.ry * age, s.rz * age);
      _o.scale.setScalar(Math.max(0.0001, s.size * k * k));
      _o.updateMatrix();
      this.shards.setMatrixAt(i, _o.matrix);
      if (s.life <= 0) {
        _o.position.set(0, -999, 0);
        _o.scale.setScalar(0.0001);
        _o.updateMatrix();
        this.shards.setMatrixAt(i, _o.matrix);
      }
    }
    if (dirty) this.shards.instanceMatrix.needsUpdate = true;

    for (let i = this.pieces.length - 1; i >= 0; i--) {
      const p = this.pieces[i];
      p.life -= dt;
      if (p.life <= 0) {
        p.mesh.visible = false;
        this.pieces.splice(i, 1);
        this.free.push(p);
        continue;
      }
      const t = 1 - p.life / p.dur; // 0 → 1
      // some acelerando no fim (fica "estalado", não apagando devagar)
      p.mat.opacity = p.op * (1 - t) * (1 - t);
      if (p.kind === "beam") {
        // 10.9GT — abre rápido, gira devagar, some no fim
        const s = p.s0 + (p.s1 - p.s0) * Math.min(1, t * 3);
        p.mesh.scale.set(s, 6, s);
        p.mesh.rotation.y += dt * 1.2;
        p.mat.opacity = p.op * Math.min(1, t * 5) * (1 - t);
        continue;
      }
      if (p.kind === "blade") {
        // 10.9AZ — a lâmina varre em Y (em pé) e cresce só no raio, mantendo a altura
        const s = p.s0 + (p.s1 - p.s0) * t;
        p.mesh.scale.x = s; p.mesh.scale.z = s;
        p.mesh.rotation.y = p.a0 + p.sweep * t;
        continue;
      }
      p.mesh.scale.setScalar(p.s0 + (p.s1 - p.s0) * t);
      if (p.kind === "slash") p.mesh.rotation.z = Math.PI / 2 - (p.a0 + p.sweep * t);
    }
  }
}
